import { MagnifyingGlass } from "@phosphor-icons/react";
import { Badge, Flex, Text, TextField } from "@radix-ui/themes";
import cx from "classnames";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MemoriesInfiniteScrollTrigger } from "./MemoriesInfiniteScrollTrigger";
import { MemoriesTable } from "./MemoriesTable";

import { debounce } from "../../../../../utils/debounce";
import { isNil } from "../../../../../utils/fp";
import { type EnterpriseMemory } from "../../../../../utils/parsers/knowledge-db";
import {
  useGetEnterpriseMemoriesQuery,
  useLoadMoreEnterpriseMemoriesMutation,
} from "../../../../../utils/queries/knowledge";
import { memoryTypeLabel } from "../memoryTypes";

const PAGE_SIZE = 100;

/**
 * Body of the read-only Memories tab. Owns its own data via react-query — it
 * owns its own data via react-query. Page 1 comes from a query keyed by the active
 * type + search; later pages are appended in local state via an imperative
 * load-more. Type-filter chip counts come from the first page's full-set
 * `typeCounts` and persist across load-more pages.
 */
export function MemoriesPanel({ initialQuery }: { initialQuery?: string }) {
  const { t } = useTranslation("ui");
  const [type, setType] = useState<string | undefined>(undefined);
  // Seeded, not controlled: the panel owns the term once the reader types.
  // It was `undefined` here, so `?tab=memories&q=...` landed on an UNFILTERED
  // list -- a citation deep link that names the memory it opens
  // (`memorySearch.ts`) sent a term nothing read, and the reader was left to
  // find the row by eye. The module's unit test stayed green throughout,
  // because the term it builds was correct; nothing consumed it.
  //
  // why a prop and not `useSearch` like `RulesPanel`: the route stays in
  // `view.tsx`, so this panel is renderable (and testable) without a router.
  const [query, setQuery] = useState<string | undefined>(initialQuery);

  const firstPage = useGetEnterpriseMemoriesQuery({
    type,
    query,
    pageSize: PAGE_SIZE,
  });
  const loadMore = useLoadMoreEnterpriseMemoriesMutation();

  const [extra, setExtra] = useState<EnterpriseMemory[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [more, setMore] = useState(false);

  // Reset the accumulated pages whenever the first page changes (type/search
  // change or refetch). react-query returns a stable data reference for cached
  // results, so this does not loop.
  useEffect(() => {
    setExtra([]);
    setCursor(firstPage.data?.nextCursor ?? null);
    setMore(firstPage.data?.more ?? false);
  }, [firstPage.data]);

  const memories = useMemo(
    () => [...(firstPage.data?.memories ?? []), ...extra],
    [firstPage.data, extra],
  );

  const typeCounts = firstPage.data?.typeCounts ?? [];
  const totalCount = typeCounts.reduce((sum, t) => sum + t.count, 0);

  const handleSearch = useMemo(
    () => debounce((q: string) => setQuery(q.trim() || undefined)),
    [],
  );

  function handleLoadMore() {
    if (isNil(cursor) || loadMore.isPending) return;
    loadMore.mutate(
      { type, query, pageSize: PAGE_SIZE, searchAfter: cursor },
      {
        onSuccess(data) {
          if (isNil(data)) return;
          setExtra((prev) => [...prev, ...data.memories]);
          setCursor(data.nextCursor);
          setMore(data.more);
        },
      },
    );
  }

  const chipClass = (active: boolean) =>
    cx("cursor-pointer rounded-full border-none bg-transparent p-0", {
      "opacity-100": active,
      "opacity-80 hover:opacity-100": !active,
    });

  return (
    <section
      aria-label={t("settings.memoriesPanel.enterpriseMemories")}
      data-testid="knowledge-database-memories-panel"
    >
      <Text
        as="p"
        size="2"
        color="gray"
        className="mb-3 block max-w-prose"
        data-testid="memories-panel-explainer"
      >
        Memories are facts Puntt has learned about your brand and past reviews;
        they inform future reviews automatically. This view is read-only.
      </Text>
      <Flex
        gap="2"
        align="center"
        wrap="wrap"
        className="mb-3"
        data-testid="memories-type-filters"
      >
        <button
          type="button"
          onClick={() => setType(undefined)}
          aria-pressed={isNil(type)}
          aria-label={t("settings.memoriesPanel.showAllMemoryTypes")}
          className={chipClass(isNil(type))}
          data-testid="memories-type-filter-all"
        >
          <Badge color="gray" variant={isNil(type) ? "solid" : "soft"}>
            All{totalCount ? ` (${totalCount})` : ""}
          </Badge>
        </button>

        {typeCounts.map((tc) => (
          <button
            key={tc.type}
            type="button"
            onClick={() => setType(tc.type)}
            aria-pressed={type === tc.type}
            aria-label={`Filter by ${memoryTypeLabel(tc.type)}`}
            className={chipClass(type === tc.type)}
            data-testid={`memories-type-filter-${tc.type}`}
          >
            <Badge color="gray" variant={type === tc.type ? "solid" : "soft"}>
              {memoryTypeLabel(tc.type)} ({tc.count})
            </Badge>
          </button>
        ))}
      </Flex>

      <Flex gap="2" className="mb-4" data-testid="memories-actions">
        <TextField.Root
          placeholder={t("settings.memoriesPanel.searchMemories")}
          defaultValue={initialQuery}
          size="1"
          variant="surface"
          aria-label={t("settings.memoriesPanel.searchMemories")}
          onInput={({ target }) => {
            handleSearch((target as HTMLInputElement).value);
          }}
          data-testid="memories-search-input"
        >
          <TextField.Slot side="right">
            <MagnifyingGlass />
          </TextField.Slot>
        </TextField.Root>
      </Flex>

      <MemoriesTable data={memories} />

      <MemoriesInfiniteScrollTrigger
        hasMore={more}
        isLoading={loadMore.isPending}
        onLoadMore={handleLoadMore}
      />
    </section>
  );
}
